import React, { memo, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { formsAuditApi } from 'app/services/api';
import EntityAuditHistoryDrawer from 'app/shared/modules/EntityAuditHistoryDrawer';
import PropTypes from 'prop-types';

import { getIsFromInternalCompany } from 'store/auth/selectors';

import FormVersionCompareModal from '../components/FormVersionCompareModal';
import {
  ARE_RAW_HISTORY_VIEWS_ENABLED,
  FORM_AUDIT_ENTITY_TYPE,
  FORM_HISTORY_AUTOMATIC_FIELDS,
  FORM_HISTORY_SHADOWED_TOGGLE,
  FORM_HISTORY_SNAPSHOT_EXCLUDED_FIELDS,
} from '../FormChangeHistory.constants';
import { FormTemplateRequestsContext } from '../FormChangeHistory.context';
import { FormChangeHistoryTestIds } from '../FormChangeHistory.testIds';
import {
  createTemplateRequests,
  getFormHiddenFields,
  getFormHistoryDrawerTitle,
  getFormRowDecoration,
  getIsScopedFormHistory,
} from '../FormChangeHistory.utils';
import useCompareWithCurrent from '../hooks/useCompareWithCurrent';

import {
  FORM_HISTORY_CHANGE_PANELS,
  FORM_TEMPLATE_RENDERERS,
  renderFormDetailExtras,
} from './FormChangeHistoryDrawer.config';

const FormChangeHistoryDrawer = ({
  formId,
  formName = '',
  isGlobalForm = false,
  open,
  onClose,
}) => {
  const isInternalViewer = useSelector(getIsFromInternalCompany);

  const templateRequests = useMemo(() => createTemplateRequests(formId), [formId]);

  const {
    compare,
    renderRowActions,
    onSelectVersion,
    onClose: onCloseCompare,
    onBack,
  } = useCompareWithCurrent();

  const isScoped = getIsScopedFormHistory({ isGlobalForm, isInternalViewer });
  const getRowDecoration = isScoped ? getFormRowDecoration : null;

  return (
    <FormTemplateRequestsContext.Provider value={templateRequests}>
      <EntityAuditHistoryDrawer
        auditClient={formsAuditApi}
        automaticFields={FORM_HISTORY_AUTOMATIC_FIELDS}
        changePanels={FORM_HISTORY_CHANGE_PANELS}
        dataTestId={FormChangeHistoryTestIds.DRAWER}
        entityId={formId}
        entityType={FORM_AUDIT_ENTITY_TYPE}
        getRowDecoration={getRowDecoration}
        hiddenFields={getFormHiddenFields}
        isSnapshotHidden={!ARE_RAW_HISTORY_VIEWS_ENABLED}
        open={open}
        renderDetailExtras={ARE_RAW_HISTORY_VIEWS_ENABLED ? renderFormDetailExtras : null}
        renderRowActions={renderRowActions}
        renderers={FORM_TEMPLATE_RENDERERS}
        shadowedToggle={isScoped ? FORM_HISTORY_SHADOWED_TOGGLE : null}
        snapshotExcludedFields={FORM_HISTORY_SNAPSHOT_EXCLUDED_FIELDS}
        title={getFormHistoryDrawerTitle(formName)}
        onClose={onClose}
      />
      <FormVersionCompareModal
        auditId={compare.auditId}
        getRowDecoration={getRowDecoration}
        isOpen={compare.isOpen}
        rows={compare.rows}
        onBack={onBack}
        onClose={onCloseCompare}
        onSelectVersion={onSelectVersion}
      />
    </FormTemplateRequestsContext.Provider>
  );
};

FormChangeHistoryDrawer.propTypes = {
  formId: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  formName: PropTypes.string,
  isGlobalForm: PropTypes.bool,
};

export default memo(FormChangeHistoryDrawer);
